import { useApi } from '@gravitate-js/the-armory'
import { useMutation, type UseMutationResult, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { useQueryClient } from '@tanstack/react-query'
import { configuredParams } from '../useMetadata/types'
import type {
  // Common types
  BookType,
  // UI component types
  FeeType,
  ProductGroup as UIProductGroup,
  Holiday,
  // Ratebook types
  Ratebook,
  CopyRatebookRequest,
  BaseFreightRate,
  BaseFreightGroup,
  CreateBaseFreightRateRequest,
  UpdateBaseFreightRateRequest,
  UpdateBaseFreightDetailsRequest,
  // Point-to-point types
  PointToPoint,
  CreatePointToPointRequest,
  CreatePointToPointRateRequest,
  UpdatePointToPointRateRequest,
  EditPointToPointRateRequest,
  DeletePointToPointRateRequest,
  // Surcharge types
  Surcharge,
  SurchargeRate,
  CreateSurchargeRequest,
  DisableSurchargeRequest,
  // Accessorial types
  AutomaticAccessorial,
  ManualAccessorial,
  CreateManualAccessorialRequest,
  CreateManualAccessorialRateRequest,
  UpdateManualAccessorialRateRequest,
  CreateAutomaticAccessorialRateRequest,
  DeleteManualAccessorialRateRequest,
  DisableManualAccessorialRequest,
  DisableAutomaticAccessorialRequest,
  CreateAutomaticAccessorialPayload,
} from './types'

// Add these interfaces near the other interfaces
interface CreateRegionRequest {
  name: string
}

interface DeleteRegionRequest {
  region_id: string
}
export interface Destination {
  id: string
  name: string
  short_name: string
  market: string
  market_id: string
  freight_region_name: string | null
  freight_region_id: string | null
  type: string
  lat: number
  lon: number
  address: string
  phone: string | null
  city: string
  state: string
  active: boolean
  postal_code: string
  source_id: string | null
  source_system: string
  source_system_id: string
  timezone: string
  supply_zones: []
  updated_on: string | null
  cards: []
  dwells: {
    product_group: string
    set_dwell: number
    dwell_override: string | null
    override_expiry: string | null
  }[]
  requires_card: boolean
  geofence: []
  supply_map: []
  extra_data: Record<string, unknown>
  freight_customer_name: string
  freight_customer_id: string
}
export interface Origin {
  id: string
  name: string
  short_name: string
  market: string
  market_id: string
  freight_region_name: string | null
  freight_region_id: string | null
  type: string
  lat: number
  lon: number
  address: string
  phone: string | null
  city: string
  state: string
  active: boolean
  postal_code: string | null
  source_id: string
  source_system: string
  source_system_id: string
  timezone: string
  supply_zones: []
  updated_on: string
  cards: []
  dwells: []
  requires_card: boolean
  geofence: []
  supply_map: []
  extra_data: Record<string, unknown>
  activity_threshold: number
  certification_trigger: string
  days_valid: number
  warning_threshold: number
  dispatch_impact: string
  carding_instructions: string
}

export interface Region {
  id: string
  name: string
}
export interface Market {
  id: string
  name: string
}
interface ProductHierarchy {
  [key: string]: {
    id: string
    name: string
  }[]
}
type BillingFrequency = 'Monthly' | 'Weekly' | 'Daily'
export type InvoiceTerm = { name: 'Net 30' | 'Net 30' | 'Net 60' | 'Net 45'; days: number }
type InvoiceGrouping = 'By Billing Period' | 'By Order' | 'Base vs. Accessorial' | 'Base vs. Assessorial'
type FreightMethod = 'longest_leg' | 'sum_of_legs'
type FreightDateMethod = 'load_date' | 'delivery_date'
type ProductMinimum = 'Gasoline' | 'Diesel' | 'Ethanol' | 'BioDiesel' | 'Gas and Diesel'
type RateUnit = 'trucks' | 'gallons' | 'liters'
type BasicFeeType =
  | 'Per Order'
  | 'Per Mile'
  | 'Per Hour'
  | 'Per Volume'
  | 'Pump Charge'
  | 'Base Freight %'
  | 'Base & Accessorial %'
  | 'Base & Surcharge %'
  | 'Surcharge %'
  | 'Total Freight %'
type ThresholdFeeType = 'Per Stop' | 'Per Drop' | 'Per Load' | 'Per No Load' | 'Detention' | 'Demurrage'
type ServiceTrigger = 'Weekend' | 'Holiday'
type ReasonCode = 'Customer Request' | 'Emergency Handling' | 'Revenue Protection'
interface RatebookOptions {
  book_type: BookType[]
  billing_volume_basis: string[]
  billing_frequency: BillingFrequency[]
  invoice_terms: InvoiceTerm[]
  invoice_grouping: InvoiceGrouping[]
  freight_method: FreightMethod[]
  freight_date_method: FreightDateMethod[]
  product_minimums: ProductMinimum[]
  rate_units: RateUnit[]
  accessorials: {
    basic_fee_types: BasicFeeType[]
    threshold_fee_types: ThresholdFeeType[]
    service_triggers: ServiceTrigger[]
    reason_codes: ReasonCode[]
  }
}
export interface FilteredMetadata {
  destinations: Destination[]
  freight_regions: Region[]
  product_groups: UIProductGroup[]
  origins: Origin[]
  product_hierarchy: ProductHierarchy | undefined
  markets: Market[]
  ratebook_options: RatebookOptions
}

export const useRateManagement = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  // Add useHolidayCalendarList query
  const useHolidayCalendarListQuery = (): UseQueryResult<Holiday[]> =>
    useQuery({
      queryKey: ['holidayCalendar'],
      queryFn: async (): Promise<Holiday[]> => {
        try {
          const response = await api.post('payroll/holiday_calendar/list')
          return response
        } catch (error) {
          console.error('Failed to fetch holiday calendar:', error)
          return []
        }
      },
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  // Add Point to Point Import/Export functions that work with ratebook filtering
  const exportPointToPointRates = (ratebook?: Ratebook | { book_type: BookType }) => {
    const params = new URLSearchParams()
    if (ratebook) {
      if ('counterparty_id' in ratebook) {
        params.append('counterparty_id', ratebook.counterparty_id)
        if ('counterparty_name' in ratebook) {
          params.append('counterparty', ratebook.counterparty_name)
        }
      }
      params.append('book_type', ratebook.book_type)
    }

    const baseURL = getAPIURL()
    const url = `${baseURL}freight/point_to_point/export?${params.toString()}`

    // Make the request with authorization headers but without credentials
    return fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      // Remove credentials: 'include' to avoid CORS issues
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.blob()
      })
      .then((blob) => {
        // Return in same format as api.postBlob for compatibility
        return { data: blob }
      })
      .catch((error) => {
        throw error
      })
  }

  const importPointToPointRates = (file: FormData, ratebook?: Ratebook) => {
    // Create the URL with query parameters if ratebook is provided
    const endpoint = 'freight/point_to_point/import'
    const queryParams = new URLSearchParams()

    if (ratebook) {
      if (ratebook._id) queryParams.append('ratebook_id', ratebook._id)
      queryParams.append('counterparty_id', ratebook.counterparty_id)
      // Include counterparty name to ensure proper mapping
      queryParams.append('counterparty', ratebook.counterparty_name)
      queryParams.append('book_type', ratebook.book_type)
    }

    // Make the request
    return api.postFormData(endpoint, file, { queryParams })
  }

  const useFilteredMetadataQuery = (ratebook: Ratebook): UseQueryResult<FilteredMetadata> =>
    useQuery({
      queryKey: ['filteredMetadata', ratebook.book_type, ratebook.counterparty_id],
      queryFn: async () => {
        const response = await api.post('metadata', configuredParams.ratebook_options, {
          ignoreDefaults: true,
        })

        if (ratebook.book_type === 'Cost') {
          return response
        }

        response.destinations = response.destinations.filter(
          (destination: { freight_customer_id: string }) => destination.freight_customer_id === ratebook.counterparty_id
        )

        const customerRegions = [
          ...new Set(
            response.destinations
              .filter((destination: { freight_region_id: string }) => !!destination.freight_region_id)
              .map((destination: { freight_region_id: string }) => destination.freight_region_id)
          ),
        ]

        response.freight_regions = customerRegions
          .map((region_id) => response.freight_regions.find((region: Region) => region.id === region_id))
          .filter((region) => region != null)

        return response
      },
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  // Point to Point Overview
  const usePointToPointsQuery = (ratebook: Ratebook): UseQueryResult<PointToPoint[]> =>
    useQuery({
      queryKey: ['ratebook/p2p', ratebook._id],
      queryFn: (): Promise<PointToPoint[]> =>
        api.post('freight/point_to_point/overview', {
          counterparty_id: ratebook.counterparty_id,
          book_type: ratebook.book_type,
        }),
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  type BaseFreightQueryResponse = {
    row_count: number
    rows: BaseFreightGroup[]
  }
  // Base Freight Query
  const useBaseFreightQuery = (ratebook?: Ratebook): UseQueryResult<BaseFreightQueryResponse> =>
    useQuery({
      queryKey: ['truck_rate/overview', ratebook?.counterparty_id],
      queryFn: (): Promise<BaseFreightQueryResponse> =>
        api.post('freight/base_freight/overview', {
          counterparty_id: ratebook?.counterparty_id,
          book_type: ratebook?.book_type,
        }),
      staleTime: 1000 * 60 * 60, // 1 hour
      enabled: !!ratebook,
    })

  // Base Freight Create
  const useCreateBaseFreight = (): UseMutationResult<BaseFreightGroup, Error, CreateBaseFreightRateRequest> =>
    useMutation({
      mutationFn: async (data: CreateBaseFreightRateRequest): Promise<BaseFreightGroup> =>
        api.post('freight/base_freight/create', data, {
          ignoreDefaults: true,
        }),
    })

  const useUpdateBaseFreight = (): UseMutationResult<BaseFreightRate, Error, UpdateBaseFreightRateRequest> =>
    useMutation({
      mutationFn: async (data: UpdateBaseFreightRateRequest): Promise<BaseFreightRate> => {
        const response = await api.post('freight/base_freight/set_rates', data, { ignoreDefaults: true })
        return response
      },
    })

  // New mutation for updating base freight details
  const useUpdateBaseFreightDetails = (): UseMutationResult<BaseFreightRate, Error, UpdateBaseFreightDetailsRequest> =>
    useMutation({
      mutationFn: async (data: UpdateBaseFreightDetailsRequest): Promise<BaseFreightRate> => {
        // The endpoint is /update, not /set_details as previously applied
        const response = await api.post('freight/base_freight/update', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  // Point to point Create
  const useCreatePointToPoint = (): UseMutationResult<PointToPoint, Error, CreatePointToPointRequest> =>
    useMutation({
      mutationFn: async (data) => {
        const response = await api.post('freight/point_to_point/create', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  // Point Rate Schedule

  // Surcharge Overview
  const useSurchargesByRatebookQuery = ({
    counterparty_id,
    book_type,
  }: {
    counterparty_id: string
    book_type: string
  }): UseQueryResult<Surcharge[]> =>
    useQuery({
      queryKey: ['surcharges', counterparty_id, book_type],
      queryFn: () =>
        api.post<Surcharge[]>('freight/surcharge/overview', {
          counterparty_id,
          book_type,
        }),

      refetchOnWindowFocus: false,
      enabled: !!counterparty_id && !!book_type,
      retry(failureCount, error) {
        if (failureCount >= 3) {
          return false
        }
        return true
      },
    })

  // Surcharge Create
  const useCreateSurcharge = (): UseMutationResult<Surcharge, Error, CreateSurchargeRequest> =>
    useMutation({
      mutationFn: (data: CreateSurchargeRequest): Promise<Surcharge> => api.post('freight/surcharge/upsert', data),
    })

  const useDeletePointToPointRate = (): UseMutationResult<PointToPoint, Error, DeletePointToPointRateRequest> =>
    useMutation({
      mutationFn: async (data) => {
        try {
          return api.post('freight/point_to_point/rate/delete', data)
        } catch (error) {
          console.error('Error deleting point-to-point rate:', error)
          throw error
        }
      },
    })

  const useCreatePointToPointRate = (): UseMutationResult<PointToPoint, Error, CreatePointToPointRateRequest> =>
    useMutation({
      mutationFn: async (data) => {
        const response = await api.post('freight/point_to_point/rate/create', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  const useCreatePointToPointNew = (): UseMutationResult<PointToPoint, Error, CreatePointToPointRequest> =>
    useMutation({
      mutationFn: async (data) => {
        const response = await api.post('freight/point_to_point/upsert', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  const useUpdatePointToPointRate = (): UseMutationResult<PointToPoint, Error, UpdatePointToPointRateRequest> =>
    useMutation({
      mutationFn: async (data) => {
        const response = await api.post('freight/point_to_point/rate/update', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  const useEditPointToPointRate = (): UseMutationResult<PointToPoint, Error, EditPointToPointRateRequest> =>
    useMutation({
      mutationFn: async (data) => {
        const response = await api.post('freight/point_to_point/rate/create', data, {
          ignoreDefaults: true,
        })
        return response
      },
    })

  const useCreateBaseFreightRate = () => {
    return useMutation<BaseFreightRate, Error, CreateBaseFreightRateRequest>({
      mutationFn: async (data) => {
        const response = await api.post('freight/base_freight/create', data)
        return response.data
      },
    })
  }

  const useDeleteBaseFreightRate = () => {
    return useMutation<void, Error, { id: string }>({
      mutationFn: async (data) => {
        await api.post('freight/base_freight/delete', data)
      },
    })
  }

  // FeeTypes endpoint
  const useFeeTypesQuery = (): UseQueryResult<FeeType[]> =>
    useQuery({
      queryKey: ['fee_types'],
      queryFn: async (): Promise<FeeType[]> => {
        // Since this endpoint doesn't exist, return empty array without making API call
        return []
      },
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  // Manual Accessorials endpoints
  const useManualAccessorialsQuery = ({
    counterparty_id,
    book_type,
  }: {
    counterparty_id: string | null
    book_type: string
  }): UseQueryResult<ManualAccessorial[]> =>
    useQuery({
      queryKey: ['manual_accessorials', counterparty_id],
      queryFn: async (): Promise<ManualAccessorial[]> => {
        try {
          const response = await api.post('freight/accessorial/manual/overview', {
            counterparty_id,
            book_type,
          })

          return response
        } catch (error) {
          return []
        }
      },
      enabled: !!counterparty_id && !!book_type,
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  const useFilteredAccessorialsQuery = ({
    order_id,
    book_type,
    customer_id,
  }: {
    order_id: string | null
    book_type: string
    customer_id?: string | null
  }): UseQueryResult<ManualAccessorial[]> =>
    useQuery({
      queryKey: ['filtered_accessorials', order_id, book_type, customer_id],
      queryFn: async (): Promise<ManualAccessorial[]> => {
        try {
          const params: Partial<ManualAccessorial> = {
            order_id: order_id || '',
            book_type: book_type,
            customer_id: customer_id || '',
          }
          const response = await api.post('freight/transaction/available_accessorials', undefined, {
            queryParams: new URLSearchParams(params as Record<string, string>),
          })

          return response
        } catch (error) {
          return []
        }
      },
      enabled: !!order_id && !!book_type,
      staleTime: 1000 * 60 * 60, // 1 hour
    })

  const useCreateManualAccessorial = (): UseMutationResult<ManualAccessorial, Error, CreateManualAccessorialRequest> =>
    useMutation({
      mutationFn: async (data: CreateManualAccessorialRequest): Promise<ManualAccessorial> => {
        const response = await api.post('freight/accessorial/manual/upsert', data)
        return response.data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['manual_accessorials'],
        })
      },
    })

  const useCreateManualAccessorialRate = (): UseMutationResult<
    ManualAccessorial,
    Error,
    CreateManualAccessorialRateRequest
  > =>
    useMutation({
      mutationFn: (data: CreateManualAccessorialRateRequest): Promise<ManualAccessorial> => {
        return api.post('freight/accessorial/manual/rate/create', {
          accessorial_id: data.accessorial_id,
          effective_from_date: data.effective_from_date,
          effective_to_date: data.effective_to_date,
          rate: data.rate,
        })
      },
    })

  const useEditManualAccessorialRate = (): UseMutationResult<
    ManualAccessorial,
    Error,
    UpdateManualAccessorialRateRequest
  > =>
    useMutation({
      mutationFn: (data: UpdateManualAccessorialRateRequest): Promise<ManualAccessorial> => {
        return api.post('freight/accessorial/manual/rate/update', data)
      },
    })

  const useDeleteManualAccessorialRate = (): UseMutationResult<
    ManualAccessorial,
    Error,
    DeleteManualAccessorialRateRequest
  > =>
    useMutation({
      mutationFn: (data: DeleteManualAccessorialRateRequest): Promise<ManualAccessorial> => {
        return api.post('freight/accessorial/manual/rate/delete', {
          accessorial_id: data.accessorial_id,
          effective_date: data.effective_date,
        })
      },
    })

  const useDisableManualAccessorial = (): UseMutationResult<
    ManualAccessorial,
    Error,
    DisableManualAccessorialRequest
  > =>
    useMutation({
      mutationFn: (data: DisableManualAccessorialRequest): Promise<ManualAccessorial> =>
        api.post('freight/accessorial/manual/disable', undefined, {
          queryParams: new URLSearchParams({
            accessorial_id: data.accessorial_id,
            ...(data.as_of && { as_of: data.as_of }),
          }),
        }),
    })

  const useDisableSurcharge = (): UseMutationResult<Surcharge, Error, DisableSurchargeRequest> =>
    useMutation({
      mutationFn: async (data: DisableSurchargeRequest): Promise<Surcharge> => {
        const params = new URLSearchParams()
        params.append('surcharge_id', data.surcharge_id)

        // Format the date as YYYY-MM-DD if it exists
        if (data.effective_from) {
          // Handle ISO string or Date object by extracting just the date portion
          const dateStr =
            typeof data.effective_from === 'string'
              ? data.effective_from.split('T')[0]
              : new Date(data.effective_from).toISOString().split('T')[0]
          params.append('effective_from', dateStr)
        }

        try {
          return await api.post('freight/surcharge/disable', undefined, {
            queryParams: params,
          })
        } catch (error) {
          console.error('Error disabling surcharge:', error)
          throw error
        }
      },
    })

  const useDisableAutomaticAccessorial = (): UseMutationResult<
    AutomaticAccessorial,
    Error,
    DisableAutomaticAccessorialRequest
  > =>
    useMutation({
      mutationKey: ['disableAutomaticAccessorial'],
      mutationFn: (data: DisableAutomaticAccessorialRequest): Promise<AutomaticAccessorial> =>
        api.post('freight/accessorial/automatic/disable', undefined, {
          queryParams: new URLSearchParams({
            accessorial_id: data.accessorial_id,
            as_of: data.as_of,
          }),
        }),
    })

  const useCreateAutomaticAccessorialRate = (): UseMutationResult<
    AutomaticAccessorial,
    Error,
    CreateAutomaticAccessorialRateRequest
  > =>
    useMutation({
      mutationKey: ['createAutomaticAccessorialRate'],
      mutationFn: async (data: CreateAutomaticAccessorialRateRequest) => {
        return api.post('freight/accessorial/automatic/rate/create', {
          accessorial_id: data.accessorial_id,
          effective_from_date: data.effective_from_date,
          effective_to_date: data.effective_to_date,
          rate: data.rate,
        })
      },
    })

  // Add new deleteAutomaticAccessorialRate mutation hook
  const useDeleteAutomaticAccessorialRate = (): UseMutationResult<
    AutomaticAccessorial,
    Error,
    { accessorial_id: string; effective_date: string }
  > =>
    useMutation({
      mutationKey: ['deleteAutomaticAccessorialRate'],
      mutationFn: async (data: {
        accessorial_id: string
        effective_date: string
      }) => {
        return api.post('freight/accessorial/automatic/rate/delete', {
          accessorial_id: data.accessorial_id,
          effective_date: data.effective_date,
        })
      },
    })

  // Add new updateAutomaticAccessorialRate mutation hook
  const useUpdateAutomaticAccessorialRate = (): UseMutationResult<
    AutomaticAccessorial,
    Error,
    {
      accessorial_id: string
      effective_from_date: string
      rate: number
      effective_to_date?: string
      rate_id: string
    }
  > =>
    useMutation({
      mutationKey: ['updateAutomaticAccessorialRate'],
      mutationFn: async (data) => {
        const payload = {
          accessorial_id: data.accessorial_id,
          effective_from_date: data.effective_from_date,
          rate: data.rate,
          rate_id: data.rate_id,
          ...(data.effective_to_date && {
            effective_to_date: data.effective_to_date,
          }),
        }
        return api.post('freight/accessorial/automatic/rate/update', payload)
      },
    })

  const useAutomaticAccessorialsQuery = (ratebook: Ratebook): UseQueryResult<AutomaticAccessorial[]> =>
    useQuery({
      queryKey: ['automaticAccessorials', ratebook._id],
      queryFn: async () =>
        api.post('freight/accessorial/automatic/overview', {
          counterparty_id: ratebook.counterparty_id,
          book_type: ratebook.book_type,
        }),
      refetchOnWindowFocus: false,
      enabled: !!ratebook._id,
    })

  const useCreateAutomaticAccessorialMutation = () =>
    useMutation({
      mutationKey: ['createAutomaticAccessorial'],
      mutationFn: async (data: CreateAutomaticAccessorialPayload) => {
        return api.post('freight/accessorial/automatic/upsert', data)
      },
    })

  const useTogglePointToPointStatus = (): UseMutationResult<
    PointToPoint,
    Error,
    {
      point_to_point_id: string
      effective_date: string
      current_status: boolean
    }
  > =>
    useMutation({
      mutationFn: async (data) => {
        try {
          // Get the point-to-point data from the queryClient's cache
          const pointToPointsData = queryClient.getQueryData<PointToPoint[]>(['ratebook/p2p']) || []
          const pointToPointData = pointToPointsData.find(
            (p) => p._id === data.point_to_point_id || p.id === data.point_to_point_id
          )

          // Create the query parameters
          const queryParams = new URLSearchParams({
            p2p_id: data.point_to_point_id,
            effective_date: data.effective_date,
            status: 'true',
            counterparty: pointToPointData?.counterparty || '',
          })

          // Only use the disable endpoint
          return api.post('freight/point_to_point/disable', undefined, {
            queryParams,
          })
        } catch (error) {
          console.error('Error toggling point-to-point status:', error)
          throw error
        }
      },
    })

  // Surcharge rate Create
  const useCreateSurchargeRate = (): UseMutationResult<
    SurchargeRate,
    Error,
    {
      surcharge_id: string
      effective_date: string
      effective_to?: string
      surcharge: number
    }
  > =>
    useMutation({
      mutationFn: (data) => api.post('freight/surcharge/rate/create', data),
    })

  // surcharge update rate
  const useEditSurchargeRate = (): UseMutationResult<
    Surcharge,
    Error,
    {
      surcharge_id: string
      effective_date: string
      effective_to?: string
      surcharge: number
      rate_id: string
    }
  > =>
    useMutation({
      mutationFn: (data) => api.post('freight/surcharge/rate/update', data),
    })

  // surcharge delete rate
  const useDeleteSurchargeRate = (): UseMutationResult<
    Surcharge,
    Error,
    {
      surcharge_id: string
      effective_date: string
      effective_to?: string | null
    }
  > =>
    useMutation({
      mutationFn: (data) => api.post('freight/surcharge/rate/delete', data),
    })

  // Add these new hooks inside useRateManagement
  const useCreateRegion = (): UseMutationResult<Region, Error, CreateRegionRequest> =>
    useMutation({
      mutationFn: async (data: CreateRegionRequest): Promise<Region> => {
        const response = await api.post('freight/region/create', {
          name: data.name,
        })
        return {
          id: response._id,
          name: response.name,
        }
      },
    })
  const useRenameRegion = (): UseMutationResult<Region, Error, { region_id: string; new_name: string }> =>
    useMutation({
      mutationFn: async (data: {
        region_id: string
        new_name: string
      }): Promise<Region> => {
        const response = await api.post('freight/region/rename', undefined, {
          queryParams: new URLSearchParams({
            region_id: data.region_id,
            new_name: data.new_name,
          }),
        })
        return response
      },
    })

  const useDeleteRegion = (): UseMutationResult<string, Error, DeleteRegionRequest> =>
    useMutation({
      mutationFn: (data: DeleteRegionRequest): Promise<string> =>
        api.post('freight/region/delete', undefined, {
          queryParams: new URLSearchParams({ region_id: data.region_id }),
        }),
    })

  // Export/Import functions for Surcharges
  const exportSurcharges = (ratebook?: Ratebook | { book_type: BookType }) => {
    const params = new URLSearchParams()
    if (ratebook) {
      if ('counterparty_id' in ratebook) {
        params.append('counterparty_id', ratebook.counterparty_id)
      }
      params.append('book_type', ratebook.book_type)
    }

    const baseURL = getAPIURL()
    const url = `${baseURL}freight/surcharge/export?${params.toString()}`

    return fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.blob()
      })
      .then((blob) => {
        return { data: blob }
      })
      .catch((error) => {
        throw error
      })
  }

  const importSurcharges = (file: FormData, ratebook?: Ratebook) => {
    const endpoint = 'freight/surcharge/import'
    const queryParams = new URLSearchParams()

    if (ratebook) {
      if (ratebook._id) queryParams.append('ratebook_id', ratebook._id)
      queryParams.append('counterparty_id', ratebook.counterparty_id)
      queryParams.append('book_type', ratebook.book_type)
    }

    return api.postFormData(endpoint, file, { queryParams })
  }

  // Export/Import functions for Base Freight (Freight Tables)
  const exportBaseFreight = (ratebook?: Ratebook | { book_type: BookType }) => {
    const params = new URLSearchParams()
    if (ratebook) {
      if ('counterparty_id' in ratebook) {
        params.append('counterparty_id', ratebook.counterparty_id)
      }
      params.append('book_type', ratebook.book_type)
    }

    const baseURL = getAPIURL()
    const url = `${baseURL}freight/base_freight/export?${params.toString()}`

    return fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.blob()
      })
      .then((blob) => {
        return { data: blob }
      })
      .catch((error) => {
        throw error
      })
  }

  const importBaseFreight = async (file: FormData, ratebook?: Ratebook) => {
    const endpoint = 'freight/base_freight/import'
    const queryParams = new URLSearchParams()

    if (ratebook) {
      if (ratebook._id) queryParams.append('ratebook_id', ratebook._id)
      queryParams.append('counterparty_id', ratebook.counterparty_id)
      queryParams.append('book_type', ratebook.book_type)
    }

    return api.postFormData(endpoint, file, { queryParams })
  }

  return {
    usePointToPointsQuery,
    useCreatePointToPoint,
    useSurchargesByRatebookQuery,
    useCreateSurcharge,
    useCreateSurchargeRate,
    useDeleteSurchargeRate,
    useUpdateBaseFreight,
    useUpdateBaseFreightDetails,
    useEditSurchargeRate,
    useBaseFreightQuery,
    useCreateBaseFreight,
    useDeletePointToPointRate,
    useCreatePointToPointRate,
    useCreatePointToPointNew,
    useUpdatePointToPointRate,
    useEditPointToPointRate,
    useCreateBaseFreightRate,
    useDeleteBaseFreightRate,
    useRenameRegion,
    useFilteredMetadataQuery,
    useHolidayCalendarListQuery,
    // Fee Types
    useFeeTypesQuery,
    // Manual Accessorials
    useManualAccessorialsQuery,
    useFilteredAccessorialsQuery,
    useCreateManualAccessorial,
    useCreateManualAccessorialRate,
    useEditManualAccessorialRate,
    useDeleteManualAccessorialRate,
    useDisableManualAccessorial,
    useDisableSurcharge,
    useCreateAutomaticAccessorialRate,
    useDeleteAutomaticAccessorialRate,
    useUpdateAutomaticAccessorialRate,
    useAutomaticAccessorialsQuery,
    useCreateAutomaticAccessorialMutation,
    useTogglePointToPointStatus,
    useDisableAutomaticAccessorial,
    useCreateRegion,
    useDeleteRegion,
    // Exposed functions for import/export
    exportPointToPointRates,
    importPointToPointRates,
    exportSurcharges,
    importSurcharges,
    exportBaseFreight,
    importBaseFreight,
  }
}

export const useRatebooks = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })

  const useRatebooksQuery = (type?: 'customer' | 'carrier') =>
    useQuery({
      queryKey: ['ratebooksQuery', type],
      queryFn: async ({ queryKey }) => {
        const response = await api.post('freight/ratebook/overview', undefined, {
          queryParams: new URLSearchParams({
            book_type: queryKey[1] === 'customer' ? 'Revenue' : 'Cost',
          }),
        })
        // Transform response to use id instead of _id and add addresses
        return response.map((ratebook: Ratebook) => ({
          ...ratebook,
          // eslint-disable-next-line no-underscore-dangle
          id: ratebook._id,
        }))
      },
      refetchOnWindowFocus: false,
    })

  const useRatebookQuery = (ratebookId: string) =>
    useQuery({
      queryKey: ['ratebook', ratebookId],
      queryFn: async () => {
        const response = await api.post('freight/ratebook/by_id', undefined, {
          queryParams: new URLSearchParams({ ratebook_id: ratebookId }),
        })

        return {
          ...response,
          // eslint-disable-next-line no-underscore-dangle
          id: response._id,
        }
      },
      enabled: !!ratebookId,
      refetchOnWindowFocus: false,
    })

  // Handles creating NEW ratebooks or UPDATING existing ones
  const useCreateRatebookMutation = (): UseMutationResult<Ratebook, Error, Ratebook> =>
    useMutation({
      mutationFn: (payload: Ratebook) => {
        const response = api.post('freight/ratebook/upsert', payload)
        return response.then((res: Ratebook) => ({
          ...res,
          // eslint-disable-next-line no-underscore-dangle
          id: res._id,
        }))
      },
    })

  // Handles COPYING an existing ratebook to a new counterparty
  const useCopyRatebookMutation = (): UseMutationResult<Ratebook, Error, CopyRatebookRequest> =>
    useMutation({
      mutationFn: (payload: CopyRatebookRequest) => {
        const queryParams = new URLSearchParams({
          source_ratebook_id: payload.source_ratebook_id,
          new_counterparty_id: payload.new_counterparty_id,
        })
        // Assuming the body might just need the name and type for the new book,
        // but the core identifiers are in the query params. Adjust if API differs.
        const body = {
          new_counterparty_name: payload.new_counterparty_name,
          book_type: payload.book_type,
        }
        const response = api.post('freight/ratebook/copy', body, {
          queryParams,
        })
        return response.then((res: Ratebook) => ({
          ...res,
          // eslint-disable-next-line no-underscore-dangle
          id: res._id,
        }))
      },
    })

  const useSetRatebookHoldMutation = () =>
    useMutation({
      mutationFn: (ratebookId: string) => {
        return api.post('freight/ratebook/set_hold', undefined, {
          queryParams: new URLSearchParams({ ratebook_id: ratebookId }),
        })
      },
    })

  const useRemoveRatebookHoldMutation = () =>
    useMutation({
      mutationFn: (ratebookId: string) => {
        return api.post('freight/ratebook/remove_hold', undefined, {
          queryParams: new URLSearchParams({ ratebook_id: ratebookId }),
        })
      },
    })

  return {
    useRatebooksQuery,
    useRatebookQuery,
    useCreateRatebookMutation, // Use for Create (new) and Update
    useCopyRatebookMutation, // Use specifically for Copy
    useSetRatebookHoldMutation,
    useRemoveRatebookHoldMutation,
  }
}
